import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { State } from "../state";
import styles from "./about.module.css";
import Seo from "./Seo";

/**
 * About page: the Shift Tracker story (built by operators, dogfooded on our own
 * station), the control-not-trust principle, what the product does, why stations
 * choose it, and a CTA into contact + the guides (blog). All copy is i18n-driven
 * from languageData.about; nothing here is hardcoded.
 */
function AboutPage(): JSX.Element {
  const { data: languageData, appLanguage } = useSelector(
    (state: State) => state.languageReducer
  );
  const aboutText = languageData?.about;
  const langPrefix = appLanguage === "en" ? "/en" : "";

  const storyParagraphs = aboutText?.story?.paragraphs ?? [];
  const offerItems = aboutText?.offer?.items ?? [];
  const whyItems = aboutText?.why?.items ?? [];

  return (
    <div className={styles.container}>
      <Seo path="/about" />

      {/* Hero */}
      <section className={styles.hero}>
        <h1 className={styles.title}>{aboutText?.hero?.title}</h1>
        <p className={styles.subtitle}>{aboutText?.hero?.subtitle}</p>
      </section>

      {/* Our story */}
      <section className={styles.story}>
        <h2 className={styles.sectionTitle}>{aboutText?.story?.title}</h2>
        {storyParagraphs.map((paragraph, index) => (
          <p key={index} className={styles.storyText}>
            {paragraph}
          </p>
        ))}
      </section>

      {/* Guiding principle */}
      <section className={styles.principle}>
        <h2 className={styles.principleTitle}>{aboutText?.principle?.title}</h2>
        <p className={styles.principleText}>{aboutText?.principle?.text}</p>
      </section>

      {/* What Shift Tracker does */}
      <section className={styles.offer}>
        <h2 className={styles.sectionTitle}>{aboutText?.offer?.title}</h2>
        <div className={styles.offerGrid}>
          {offerItems.map((item, index) => (
            <div key={index} className={styles.offerItem}>
              <span className={styles.icon}>{item.icon}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why stations choose Shift Tracker */}
      <section className={styles.why}>
        <h2 className={styles.sectionTitle}>{aboutText?.why?.title}</h2>
        <div className={styles.whyGrid}>
          {whyItems.map((item, index) => (
            <div key={index} className={styles.whyItem}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to action */}
      <section className={styles.cta}>
        <h2>{aboutText?.cta?.title}</h2>
        <p>{aboutText?.cta?.subtitle}</p>
        <div className={styles.ctaButtons}>
          <NavLink to={`${langPrefix}/contact/`} className={styles.ctaButton}>
            {aboutText?.cta?.button}
          </NavLink>
          <NavLink to={`${langPrefix}/blog/`} className={styles.ctaSecondary}>
            {aboutText?.cta?.secondary}
          </NavLink>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
